import { FC, useCallback, useEffect, useRef } from 'react';

import { formatCostInCents, getNullableNumberFromString, getStringFromNullableNumber } from 'utils/clickme';
import { AUTO_STRATEGY_BID_STEP_IN_CENTS, BID_MAX_LENGTH, BID_STEP } from 'utils/constants/auction';

import auctionCampaignBidFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_bid_form_field_touch';
import auctionCampaignBidPlusMinusButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_bid_plus_minus_button_click';
import {
    HSpacing,
    LoadingContextProvider,
    Skeleton,
    Text,
    useBreakpoint,
    useDebounce,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { TextStyle } from '@hh.ru/magritte-ui-typography/types';
import {
    ChevronWide2OutlinedSize16,
    ChevronWide3OutlinedSize16,
    ChevronWideOutlinedSize16,
} from '@hh.ru/magritte-ui/icon';

import { useAuctionBidValidation } from 'hooks/auction/useAuctionBidValidation';
import { useRequestCancel } from 'hooks/common/useRequestCancel';
import useTrls from 'hooks/translation/useTrls';

import { fetchBidRating } from 'components/AuctionBuilder/fetchBidRating';
import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';
import { MoneyInputWithSteps } from 'components/common/MoneyInputWithSteps/MoneyInputWithSteps';

import {
    AuctionBuilderBidErrorCode,
    auctionSelector,
    auctionSelectorNonNullable,
    BiddingStrategy,
    BidFrequencyStatus,
    setBidValue,
    setMinBidValue,
} from 'store/models/auctionData';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/AuctionBuilder/AuctionBid/AuctionBid.module.less';

const TrlKeys = {
    label: {
        [BiddingStrategy.Auto]: 'AuctionBuilder.AuctionBid.label.auto',
        [BiddingStrategy.Manual]: 'AuctionBuilder.AuctionBid.label.manual',
    },
    labelTooltip: {
        part1: 'AuctionBuilder.AuctionBid.labelTooltip.part1',
        part2: 'AuctionBuilder.AuctionBid.labelTooltip.part2',
        autoStrategy: 'AuctionBuilder.AuctionBid.labelTooltip.autoStrategy',
    },
    recomendedBid: 'AuctionBuilder.AuctionBid.recomendedBid',
    error: {
        [AuctionBuilderBidErrorCode.AutoStrategyRangeUnderflow]:
            'AuctionBuilder.AuctionBid.error.autoStrategyRangeUnderflow',
        [AuctionBuilderBidErrorCode.BidCanNotBeChangedForFixedPriceAccount]:
            'AuctionBuilder.AuctionBid.error.bidCanNotBeChangedForFixedPriceAccount',
        [AuctionBuilderBidErrorCode.BidCanNotBeMoreThanCampaignBalance]:
            'AuctionBuilder.AuctionBid.error.bidCanNotBeMoreThanCampaignBalance',
        [AuctionBuilderBidErrorCode.RangeUnderflow]: 'AuctionBuilder.AuctionBid.error.rangeUnderflow',
        [AuctionBuilderBidErrorCode.RangeOverflow]: 'AuctionBuilder.AuctionBid.error.rangeOverflow',
        default: 'AuctionBuilder.AuctionBid.error.default',
    },
    bidRating: {
        [BidFrequencyStatus.Rare]: 'AuctionBuilder.AuctionBid.bidRating.rare',
        [BidFrequencyStatus.Average]: 'AuctionBuilder.AuctionBid.bidRating.average',
        [BidFrequencyStatus.Often]: 'AuctionBuilder.AuctionBid.bidRating.often',
    },
};

const getErrorTrl = (errorCode: string): string => {
    return TrlKeys.error[errorCode as keyof typeof TrlKeys.error] || TrlKeys.error.default;
};

const BID_RATING_STATUS = {
    [BidFrequencyStatus.Rare]: {
        icon: (
            <ChevronWideOutlinedSize16
                initialColor="negative"
                backgroundStyle="negative-secondary"
                borderRadius={12}
                padding={8}
            />
        ),
        style: 'negative',
    },
    [BidFrequencyStatus.Average]: {
        icon: (
            <ChevronWide2OutlinedSize16
                initialColor="warning"
                backgroundStyle="warning-secondary"
                borderRadius={12}
                padding={8}
            />
        ),
        style: 'warning',
    },
    [BidFrequencyStatus.Often]: {
        icon: (
            <ChevronWide3OutlinedSize16
                initialColor="positive"
                backgroundStyle="positive-secondary"
                borderRadius={12}
                padding={8}
            />
        ),
        style: 'positive',
    },
};

export const AuctionBid: FC<{ loading: boolean; setLoading: (loading: boolean) => void }> = ({
    loading,
    setLoading,
}) => {
    const biddingStrategy = useSelector(auctionSelectorNonNullable.biddingStrategy);
    const { value, minValue, initialMinValue, disabled } = useSelector(auctionSelectorNonNullable.bid);
    const errorCode = useSelector(auctionSelector.errors.bid);
    const bidRating = useSelector(auctionSelector.bidRating);
    const oftenThresholdBid = bidRating?.oftenThresholdBid;
    const bidFrequencyStatus = bidRating?.bidFrequencyStatus;
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const campaignId = useSelector(auctionSelector.auctionCampaign)?.campaignId;

    const { abortRequest, abortSignal } = useRequestCancel();
    const dispatch = useDispatch();
    const userEnteredBid = useRef<number | null>(campaignId ? value : null);
    const trls = useTrls();
    const validateBid = useAuctionBidValidation();
    const { isXS } = useBreakpoint();

    const formattedMinValue = formatCostInCents(minValue);
    const errorText = errorCode ? trls(getErrorTrl(errorCode), [formattedMinValue]) : null;

    const labelTooltip = useRef({
        [BiddingStrategy.Auto]: <Text typography="paragraph-2-regular">{trls(TrlKeys.labelTooltip.autoStrategy)}</Text>,
        [BiddingStrategy.Manual]: (
            <>
                <Text typography="paragraph-2-regular">{trls(TrlKeys.labelTooltip.part1)}</Text>
                <VSpacing default={8} />
                <Text typography="paragraph-2-regular">{trls(TrlKeys.labelTooltip.part2)}</Text>
            </>
        ),
    });

    const debouncedBidRating = useRef(
        useDebounce(() => {
            abortRequest();
            void dispatch(fetchBidRating(setLoading, abortSignal()));
        }, 500)
    );

    const onBidChange = useCallback(
        (value: string) => {
            const intValue = getNullableNumberFromString(value);
            userEnteredBid.current = intValue;
            dispatch(setBidValue(intValue));
            validateBid(intValue);
            if (intValue && intValue >= minValue) {
                debouncedBidRating.current();
            }
        },
        [dispatch, minValue, validateBid]
    );

    useEffect(() => {
        if (biddingStrategy === BiddingStrategy.Auto && minValue === initialMinValue) {
            const newMinValue = initialMinValue + AUTO_STRATEGY_BID_STEP_IN_CENTS;
            dispatch(setMinBidValue(newMinValue));

            // если переключились на Auto и value был равен initialMinValue, поднимаем ставку до нового минимума
            if (value === initialMinValue) {
                dispatch(setBidValue(newMinValue));
            }
        }

        if (
            biddingStrategy === BiddingStrategy.Manual &&
            initialMinValue !== undefined &&
            minValue !== initialMinValue
        ) {
            // если переключились на Manual: восстанавливаем ставку, которую вводил пользователь,
            // либо возвращаемся к initialMinValue если пользователь ещё не редактировал ставку вручную
            const bidToRestore = userEnteredBid.current ?? initialMinValue;
            dispatch(setMinBidValue(initialMinValue));

            if (value === initialMinValue + AUTO_STRATEGY_BID_STEP_IN_CENTS) {
                dispatch(setBidValue(bidToRestore));
            }
        }
    }, [biddingStrategy, initialMinValue, minValue, value, dispatch]);

    useEffect(() => {
        validateBid(value);
    }, [minValue, value, validateBid]);

    return (
        <MagritteFormField
            label={trls(TrlKeys.label[biddingStrategy])}
            labelTooltipContent={labelTooltip.current[biddingStrategy]}
            input={
                <div className={styles.wrapper}>
                    <div className={styles.inputWrapper}>
                        <MoneyInputWithSteps
                            dataQa="auction-bid"
                            value={getStringFromNullableNumber(value)}
                            step={BID_STEP}
                            maxLength={BID_MAX_LENGTH}
                            minValue={minValue}
                            disabled={disabled}
                            invalid={!!errorText}
                            errorMessage={errorText}
                            onChange={onBidChange}
                            onMinusClick={() =>
                                auctionCampaignBidPlusMinusButtonClick({
                                    buttonName: 'auction_campaign_bid_minus',
                                    vacancyId,
                                    campaignId,
                                })
                            }
                            onPlusClick={() =>
                                auctionCampaignBidPlusMinusButtonClick({
                                    buttonName: 'auction_campaign_bid_plus',
                                    vacancyId,
                                    campaignId,
                                })
                            }
                            onBlur={() =>
                                auctionCampaignBidFormFieldTouch({ value: String(value), vacancyId, campaignId })
                            }
                            description={
                                oftenThresholdBid &&
                                bidFrequencyStatus !== BidFrequencyStatus.Often &&
                                trls(TrlKeys.recomendedBid, [formatCostInCents(oftenThresholdBid)])
                            }
                        />
                    </div>
                    {bidFrequencyStatus && (
                        <div className={styles.bidRatingStatus}>
                            {!isXS && <HSpacing default={0} />}
                            <LoadingContextProvider loading={loading}>
                                <Skeleton height={22} width={220} borderRadius={4}>
                                    {BID_RATING_STATUS[bidFrequencyStatus].icon}
                                    <Text
                                        data-qa="bid-rating-text"
                                        typography="label-2-regular"
                                        style={BID_RATING_STATUS[bidFrequencyStatus].style as TextStyle}
                                    >
                                        {trls(TrlKeys.bidRating[bidFrequencyStatus])}
                                    </Text>
                                </Skeleton>
                            </LoadingContextProvider>
                        </div>
                    )}
                </div>
            }
        />
    );
};
